import React from 'react';
import { CheckCircle2, BarChart3, Lock, Users2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PollCardProps {
  poll: {
    id: string;
    question: string;
    is_multiple_choice: boolean;
    is_anonymous: boolean;
    closes_at: string | null;
    options: Array<{
      id: string;
      option_text: string;
      vote_count: number;
    }>;
    total_votes: number;
    user_votes: string[];
  };
  onVote: (optionId: string) => void;
}

const OPTION_COLORS = [
  'from-blue-500 to-blue-600',
  'from-purple-500 to-purple-600',
  'from-emerald-500 to-emerald-600',
  'from-orange-500 to-orange-600',
  'from-pink-500 to-pink-600',
  'from-cyan-500 to-cyan-600',
  'from-amber-500 to-amber-600',
  'from-indigo-500 to-indigo-600',
];

export default function PollCard({ poll, onVote }: PollCardProps) {
  const hasVoted = poll.user_votes.length > 0;
  const isClosed = poll.closes_at ? new Date(poll.closes_at) < new Date() : false;
  const showResults = hasVoted || isClosed;

  return (
    <div className="mt-4 p-5 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 rounded-xl border border-purple-100 dark:border-purple-800/50">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
          <BarChart3 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        </div>
        <h4 className="font-semibold text-sm flex-1">{poll.question}</h4>
      </div>

      <div className="space-y-2.5">
        {poll.options.map((option, idx) => {
          const isSelected = poll.user_votes.includes(option.id);
          const percentage = poll.total_votes > 0 ? Math.round((option.vote_count / poll.total_votes) * 100) : 0;
          const colorClass = OPTION_COLORS[idx % OPTION_COLORS.length];

          return (
            <button
              key={option.id}
              onClick={() => !isClosed && onVote(option.id)}
              disabled={isClosed}
              className={cn(
                "w-full relative overflow-hidden rounded-xl border-2 p-3.5 text-left transition-all duration-300",
                isSelected
                  ? "border-purple-400 dark:border-purple-500 bg-white dark:bg-gray-900 shadow-sm"
                  : "border-transparent bg-white/80 dark:bg-gray-900/80 hover:border-purple-200 dark:hover:border-purple-700 hover:shadow-sm",
                isClosed && "cursor-default opacity-90"
              )}
            >
              {showResults && (
                <div
                  className={cn("absolute inset-y-0 left-0 bg-gradient-to-r opacity-15 transition-all duration-700 ease-out rounded-xl", colorClass)}
                  style={{ width: `${percentage}%` }}
                />
              )}
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  {isSelected ? (
                    <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-400 shrink-0" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600 shrink-0" />
                  )}
                  <span className={cn("text-sm", isSelected && "font-medium")}>{option.option_text}</span>
                </div>
                {showResults && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                      {percentage}%
                    </span>
                    <span className="text-[10px] text-gray-400">
                      ({option.vote_count})
                    </span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 px-1">
        <div className="flex items-center gap-1.5">
          <Users2 className="h-3.5 w-3.5" />
          <span className="font-medium">{poll.total_votes} {poll.total_votes === 1 ? 'voto' : 'voti'}</span>
        </div>
        <div className="flex items-center gap-3">
          {poll.is_multiple_choice && (
            <span className="flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">
              Scelta multipla
            </span>
          )}
          {poll.is_anonymous && (
            <span className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
              <Lock className="h-3 w-3" /> Anonimo
            </span>
          )}
          {isClosed && (
            <span className="text-red-500 font-semibold bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">Chiuso</span>
          )}
        </div>
      </div>
    </div>
  );
}
